import {
	DEFAULT_QUESTIONNAIRE_TEMPLATE_ID,
	DEFAULT_QUESTIONNAIRE_TEMPLATE_VERSION,
	type QuestionnaireAnswerMetadata,
	type QuestionnaireQuestion,
	type QuestionnaireQuestionPresentation,
	type QuestionnaireResponseKind,
	type QuestionnaireTemplate,
} from "../contracts"
import { aroraHrtQuestions } from "./questionnaire-content"

const questionPresentationById: Readonly<
	Record<string, QuestionnaireQuestionPresentation>
> = {
	female_blood_pressure_or_heart_disease: {
		followUpQuestion: "Is your blood pressure controlled with medication?",
	},
	female_clot_stroke_heart_or_clotting_disorder: {
		followUpQuestion:
			"Have you had a DVT, pulmonary embolism, stroke, or heart attack more than 6 months ago, or been diagnosed with a clotting disorder?",
	},
	female_hormone_sensitive_cancer: {
		followUpQuestion:
			"Have you had hormone-receptor-positive cancer that was treated and is in remission?",
	},
	female_unexplained_vaginal_bleeding: {
		followUpQuestion:
			"Have you had vaginal bleeding that has been evaluated and a cause identified?",
	},
	male_blood_pressure_or_cardiovascular_disease: {
		followUpQuestion:
			"Do you have high blood pressure that is currently controlled or managed with medication?",
	},
	male_cardiovascular_or_clot_history: {
		followUpQuestion:
			"Have you had a heart attack, stroke, or blood clot more than 6 months ago, or been diagnosed with a clotting disorder?",
	},
	male_hematocrit_polycythemia: {
		followUpQuestion: "Have you had elevated hematocrit or polycythemia that is now resolved?",
	},
	male_kidney_disease: {
		followUpQuestion: "Do you have mild to moderate kidney disease that is currently managed?",
	},
	male_liver_disease: {
		followUpQuestion: "Do you have a mild to moderate liver condition that is currently managed?",
	},
	male_prostate_or_breast_cancer: {
		followUpQuestion:
			"Have you had prostate cancer or male breast cancer that was treated and/or is in remission?",
	},
	male_psychiatric_history: {
		followUpQuestion: "Do you have a stable psychiatric condition currently managed under treatment?",
	},
	male_sleep_apnea: {
		followUpQuestion: "Is your sleep apnea currently treated with CPAP or another prescribed therapy?",
	},
	male_current_prescription_medications: { inputControl: "medication_list" },
	female_current_prescription_medications: { inputControl: "medication_list" },
}

const optionDisplayTextByQuestionId: Readonly<
	Record<string, Readonly<Record<string, string>>>
> = {
	male_blood_pressure_or_cardiovascular_disease: {
		"High blood pressure that is currently controlled or managed with medication": "Yes",
		"None of the above": "No",
	},
	male_psychiatric_history: {
		"Stable psychiatric condition currently managed under treatment": "Yes",
		"None of the above": "No",
	},
}

const aggregateItemTextByAnswer: Readonly<Record<string, string>> = {
	"Yes — I have active or untreated prostate cancer or male breast cancer":
		"Currently active or untreated prostate cancer or male breast cancer",
}

function getResponseKind(option: string): QuestionnaireResponseKind {
	const normalized = option.trim().toLowerCase()
	if (
		/^(?:no|none|never)(?:$|[\s,.:;—-])/.test(normalized) ||
		/^i (?:do not|don't)\b/.test(normalized)
	) {
		return "negative"
	}
	if (/^yes(?:$|[\s,.:;—-])/.test(normalized)) {
		return "affirmative"
	}
	return "neutral"
}

function formatAggregateItemText(answer: string): string {
	const configured = aggregateItemTextByAnswer[answer]
	if (configured !== undefined) {
		return configured
	}
	const formatted = answer
		.replace(/^Yes — I have /, "")
		.replace(/^Yes — /, "")
		.trim()
		.replace(/^active/, "Active")
		.replace(/^severe/, "Severe")
		.replace(/^currently/, "Currently")
	if (formatted.length === 0) {
		throw new Error(`Disqualifying answer has no aggregate display text: ${answer}`)
	}
	return `${formatted.charAt(0).toUpperCase()}${formatted.slice(1)}`
}

function getHeadingPresentation(question: QuestionnaireQuestion): {
	readonly headingTitle: string
	readonly headingSubtitle?: string
} {
	if (question.subtitle !== undefined) {
		return {
			headingTitle: question.question,
			headingSubtitle: question.subtitle,
		}
	}

	const rawQuestion = question.question.trim()
	const helperMatches = [...rawQuestion.matchAll(/\(([^()]+)\)/g)]
	const helperParts = helperMatches
		.map((match) => {
			const helperText = match[1]
			if (helperText === undefined) {
				return null
			}
			const trimmed = helperText.trim()
			return trimmed.length === 0 ? null : trimmed
		})
		.filter((value): value is string => value !== null)
	if (helperParts.length === 0) {
		return { headingTitle: question.question }
	}

	const headingTitle = rawQuestion
		.replace(/\s*\(([^()]+)\)/g, "")
		.replace(/\s+([?!.:,;])/g, "$1")
		.replace(/\s{2,}/g, " ")
		.trim()
	const headingSubtitle = [...new Set(helperParts)]
		.map((helperText) => `${helperText.slice(0, 1).toUpperCase()}${helperText.slice(1)}`)
		.join(" • ")
	if (headingTitle.length === 0 || headingSubtitle.length === 0) {
		return { headingTitle: question.question }
	}
	return { headingTitle, headingSubtitle }
}

function getDetailsPromptLabel(question: QuestionnaireQuestion): string | undefined {
	if (question.detailsPrompt === undefined) {
		return undefined
	}
	const lines = question.detailsPrompt
		.replace(/➤/g, "")
		.replace(/_{3,}/g, "")
		.split("\n")
		.map((line) => line.trim())
		.filter((line) => line.length > 0)
	const firstLine = lines[0]
	if (firstLine === undefined) {
		return "Details"
	}
	if (/^other:?$/i.test(firstLine)) {
		return "Please describe"
	}
	return lines.join("\n")
}

export type PresentedQuestionnaireQuestion = QuestionnaireQuestion & {
	readonly presentation: QuestionnaireQuestionPresentation & {
		readonly headingTitle: string
	}
}

export function withQuestionnairePresentation(
	question: QuestionnaireQuestion,
): PresentedQuestionnaireQuestion {
	const answerMetadata: Record<string, QuestionnaireAnswerMetadata> = {}
	const configuredDisplayText = optionDisplayTextByQuestionId[question.id]
	for (const option of question.options) {
		const existingMetadata = question.answerMetadata === undefined
			? undefined
			: question.answerMetadata[option]
		const displayText = configuredDisplayText === undefined
			? undefined
			: configuredDisplayText[option]
		const responseKind = getResponseKind(displayText === undefined ? option : displayText)
		const disqualifies = question.disqualifyingAnswers !== undefined &&
			question.disqualifyingAnswers.includes(option)
		answerMetadata[option] = {
			riskLevel: existingMetadata === undefined ? question.riskLevel : existingMetadata.riskLevel,
			source: existingMetadata === undefined ? question.source : existingMetadata.source,
			...(existingMetadata !== undefined && existingMetadata.disqualifies === true
				? { disqualifies: true }
				: {}),
			responseKind,
			...(question.type === "multiselect" && responseKind === "negative"
				? { exclusive: true }
				: {}),
			...(displayText === undefined ? {} : { displayText }),
			...(disqualifies ? { aggregateItemText: formatAggregateItemText(option) } : {}),
		}
	}

	const configuredPresentation = questionPresentationById[question.id]
	const detailsPromptLabel = getDetailsPromptLabel(question)
	const presentation = {
		...(question.presentation === undefined ? {} : question.presentation),
		...(configuredPresentation === undefined ? {} : configuredPresentation),
		...getHeadingPresentation(question),
		...(detailsPromptLabel === undefined ? {} : { detailsPromptLabel }),
	}
	return {
		...question,
		...(question.options.length === 0 ? {} : { answerMetadata }),
		presentation,
	}
}

const optimistPreAccountQuestions: QuestionnaireQuestion[] = [
	{
		id: "reason",
		question: "What brings you here today?",
		subtitle: "Select all that apply",
		type: "multiselect",
		options: [
			"Learn more about my symptoms.",
			"Test my testosterone levels.",
			"Find treatment that works.",
			"Just exploring.",
		],
		phase: "pre_account",
		gender: "all",
		riskLevel: "regular",
		source: {
			pdf: "Optimist intake",
			page: 1,
			section: "Current short questionnaire",
			questionNumber: "reason",
		},
	},
	{
		id: "symptoms",
		question: "What symptoms are affecting you most?",
		subtitle: "Select all that apply",
		type: "multiselect",
		options: [
			"Low energy/fatigue.",
			"Mood changes.",
			"Decreased libido.",
			"Muscle mass.",
			"Brain fog/concentration.",
			"Not sure, trying to learn more.",
		],
		phase: "pre_account",
		gender: "all",
		riskLevel: "regular",
		source: {
			pdf: "Optimist intake",
			page: 1,
			section: "Current short questionnaire",
			questionNumber: "symptoms",
		},
	},
	{
		id: "symptom_timing",
		question: "When did you first notice these symptoms?",
		type: "select",
		options: ["Within the last 3 months", "Within the last year", "More than a year ago"],
		phase: "pre_account",
		gender: "all",
		riskLevel: "regular",
		source: {
			pdf: "Optimist intake",
			page: 1,
			section: "Current short questionnaire",
			questionNumber: "symptom_timing",
		},
	},
	{
		id: "testosterone_tested",
		question: "Have you had your testosterone levels tested before?",
		type: "select",
		options: [
			"Yes, within the last 3 months.",
			"Yes, but more than 3 months ago.",
			"No, never tested.",
			"Not sure.",
		],
		phase: "pre_account",
		gender: "all",
		riskLevel: "regular",
		source: {
			pdf: "Optimist intake",
			page: 1,
			section: "Current short questionnaire",
			questionNumber: "testosterone_tested",
		},
	},
]

export const tSprayQualifierQuestions: QuestionnaireQuestion[] = [
	...optimistPreAccountQuestions,
	...aroraHrtQuestions,
].map(withQuestionnairePresentation)

export const tSprayQualifierQuestionnaireTemplate = {
	id: DEFAULT_QUESTIONNAIRE_TEMPLATE_ID,
	version: DEFAULT_QUESTIONNAIRE_TEMPLATE_VERSION,
	questions: tSprayQualifierQuestions,
} satisfies QuestionnaireTemplate

const questionnaireTemplateVersions = [
	tSprayQualifierQuestionnaireTemplate,
] as const satisfies readonly QuestionnaireTemplate[]

const latestQuestionnaireTemplateById = new Map<string, QuestionnaireTemplate>([
	[tSprayQualifierQuestionnaireTemplate.id, tSprayQualifierQuestionnaireTemplate],
])

const questionnaireTemplateByVersion = new Map<string, QuestionnaireTemplate>(
	questionnaireTemplateVersions.map((template) => [
		`${template.id}@${template.version}`,
		template,
	] as const),
)

export function getQuestionnaireTemplate(
	templateId: string,
	version?: string,
): QuestionnaireTemplate {
	const latestTemplate = latestQuestionnaireTemplateById.get(templateId)
	let resolvedVersion = version
	if (resolvedVersion === undefined && latestTemplate !== undefined) {
		resolvedVersion = latestTemplate.version
	}
	const template = resolvedVersion === undefined
		? undefined
		: questionnaireTemplateByVersion.get(`${templateId}@${resolvedVersion}`)
	if (template === undefined) {
		throw new Error(
			`Questionnaire template is not registered: ${templateId}@${resolvedVersion === undefined ? "latest" : resolvedVersion}`,
		)
	}
	return template
}
